import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Price } from '../../components/atoms/Price'
import { Spinner } from '../../components/atoms/Spinner'
import { OrderHistoryPopup } from '../../components/orders/OrderHistoryPopup'
import { RequestReturnModal } from '../../components/orders/RequestReturnModal'
import { Paginator } from '../../components/molecules/Paginator'
import { ROUTES, fillPath } from '../../../constants/routes.constants'
import {
  ORDERSTATUS,
  ORDERSTATUSLABEL,
  isReturnBranch,
  type OrderStatus
} from '../../../constants/order-status.constants'
import { useAccountOrders } from '../../../viewmodels/account/useAccountOrders'
import { Stagger, StaggerItem } from '../../motion'

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

/** `/account/orders` — paid + unpaid order history. */
export function AccountOrdersPage(): JSX.Element {
  const { orders, total, page, pageSize, isLoading, error, setPage, fileReturn, cancelReturn, cancelOrder } = useAccountOrders()
  const [historyOrder, setHistoryOrder] = useState<{ id: string; number: string } | null>(null)
  const [returnOrder, setReturnOrder] = useState<{ id: string; number: string } | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Orders — Uyyamadilu</title>
      </Helmet>
      <h1 className="font-heading text-h1 text-charcoal mb-6">Orders</h1>

      {cancelError !== null ? (
        <div className="mb-4">
          <Alert tone="error">{cancelError}</Alert>
        </div>
      ) : null}

      {error !== null ? (
        <Alert tone="error">{error}</Alert>
      ) : orders.length === 0 ? (
        <p className="font-body text-body text-text-secondary">
          You haven&apos;t placed any orders yet.
        </p>
      ) : (
        <>
          <Stagger as="ul" className="space-y-3" amount={0.05}>
            {orders.map((o) => (
              <StaggerItem as="li" key={o.id}>
                <Link
                  to={fillPath(ROUTES.ACCOUNTORDERDETAIL, { id: o.id })}
                  className="block rounded-xl border border-border p-5 bg-bg-primary transition-all duration-500 ease-cinematic hover:-translate-y-0.5 hover:border-forest hover:shadow-md"
                >
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-heading text-h3 text-charcoal">
                        Order {o.orderNumber}
                      </p>
                      <p className="mt-1 font-body text-body-sm text-text-secondary">
                        Placed {formatDate(o.placedAt)} · {o.lineItems.length} item
                        {o.lineItems.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="text-right">
                      <Price amount={o.total} currency={o.currency} size="md" />
                      <div className="mt-1 flex gap-2 justify-end">
                        <Badge
                          tone={isReturnBranch(o.currentStatus as OrderStatus) ? 'warning' : 'success'}
                        >
                          {ORDERSTATUSLABEL[o.currentStatus as OrderStatus] ?? o.currentStatus}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-end gap-3">
                        {o.returnable ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setReturnOrder({ id: o.id, number: o.orderNumber })
                            }}
                          >
                            Request return
                          </Button>
                        ) : null}
                        {o.currentStatus === ORDERSTATUS.RETURNPENDING ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={cancellingId === o.id}
                            onClick={async (e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setCancelError(null)
                              setCancellingId(o.id)
                              const res = await cancelReturn(o.id)
                              setCancellingId(null)
                              if (!res.ok) setCancelError(res.message)
                            }}
                          >
                            {cancellingId === o.id ? 'Cancelling…' : 'Cancel return'}
                          </Button>
                        ) : null}
                        {o.cancellable ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={cancellingOrderId === o.id}
                            onClick={async (e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setCancelError(null)
                              setCancellingOrderId(o.id)
                              const res = await cancelOrder(o.id)
                              setCancellingOrderId(null)
                              if (!res.ok) setCancelError(res.message)
                            }}
                          >
                            {cancellingOrderId === o.id ? 'Cancelling…' : 'Cancel order'}
                          </Button>
                        ) : null}
                        <button
                          type="button"
                          className="font-body text-body-xs text-forest underline-offset-2 hover:underline"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setHistoryOrder({ id: o.id, number: o.orderNumber })
                          }}
                        >
                          View history
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}
      {historyOrder !== null ? (
        <OrderHistoryPopup
          open={historyOrder !== null}
          orderId={historyOrder.id}
          orderNumber={historyOrder.number}
          scope="customer"
          onClose={() => setHistoryOrder(null)}
        />
      ) : null}
      {returnOrder !== null ? (
        <RequestReturnModal
          open={returnOrder !== null}
          orderNumber={returnOrder.number}
          onClose={() => setReturnOrder(null)}
          onSubmit={(reason) => fileReturn(returnOrder.id, reason)}
        />
      ) : null}
    </>
  )
}
